import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ViewMode, ToastMessage } from '../../types';
import Avatar from '../ui/Avatar';
import { getBillingStatus } from '../../lib/billingApi';

interface UserAvatarProps {
  viewMode: ViewMode;
  onShowToast: (toast: Omit<ToastMessage, 'id'>) => void;
}

export default function UserAvatar({ viewMode, onShowToast }: UserAvatarProps) {
  const { user, logout } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [plan, setPlan] = useState<'free' | 'pro2' | 'pro5'>('free');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const status = await getBillingStatus();
        if (mounted) setPlan(status.plan);
      } catch {
        // ignore
      }
    })();
    const handler = async () => {
      try {
        const status = await getBillingStatus();
        setPlan(status.plan);
      } catch {}
    };
    window.addEventListener('billing:updated', handler as EventListener);
    return () => { mounted = false; };
  }, []);

  const handleLogout = async () => {
    // Logout is now instant on the client; treat backend errors as non-blocking.
    logout().catch((error) => {
      console.error('Logout error:', error);
    });
    onShowToast({
      type: 'success',
      title: 'Logged out',
      description: 'You have been successfully logged out.'
    });
    setIsOpen(false);
  };

  if (!user) return null;

  const isMobile = viewMode === 'mobile';
  const ringClass = plan === 'pro5'
    ? 'ring-2 ring-blue-500'
    : plan === 'pro2'
      ? 'ring-2 ring-blue-400'
      : '';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <div className="relative">
          <Avatar 
            src={user.avatar_url} 
            name={user.name} 
            size="sm"
            className={ringClass}
          />
          {plan !== 'free' && (
            <span className="absolute -bottom-1 -right-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500 text-white">
              {plan === 'pro5' ? '5' : '2'}
            </span>
          )}
        </div>
        {!isMobile && (
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {user.name}
          </span>
        )}
        <svg 
          className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-[80]">
          {/* User Info */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="relative">
                <Avatar 
                  src={user.avatar_url} 
                  name={user.name} 
                  size="md"
                  className={ringClass}
                />
                {plan !== 'free' && (
                  <span className="absolute -bottom-1 -right-1 text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500 text-white">
                    {plan === 'pro5' ? 'Pro-5' : 'Pro-2'}
                  </span>
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {user.name}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {user.email}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="p-2">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 